// src/components/notifications/NotificationPanel.tsx
import { useEffect } from "react";
import { ActionIcon, Box, Group, ScrollArea, Stack, Text, UnstyledButton } from "@mantine/core";
import { IconBellOff, IconCheck, IconTrash } from "@tabler/icons-react";
import { useNavigate } from "@/routing/accountRouting";
import { useNotify } from "@/hooks/useNotify";
import NotificationItem from "./NotificationItem";

type Props = {
  onClose: () => void;
};

export default function NotificationPanel({ onClose }: Props) {
  const navigate = useNavigate();
  const {
    notifications,
    isLoading,
    markAsRead,
    markAllAsRead,
    dismiss,
    dismissAll,
    fetchNotifications,
  } = useNotify();

  // Fetch full list when panel opens
  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  return (
    <Box style={{ width: "100%" }}>
      {/* Header */}
      <Group justify="space-between" px="sm" py="xs" style={{ borderBottom: "1px solid var(--mantine-color-gray-2)" }}>
        <Text fw={600} size="sm">
          Notifications
        </Text>
        <Group gap={4}>
          <ActionIcon
            variant="subtle"
            color="gray"
            size="sm"
            onClick={markAllAsRead}
            aria-label="Mark all as read"
            title="Mark all as read"
          >
            <IconCheck size={16} />
          </ActionIcon>
          <ActionIcon
            variant="subtle"
            color="gray"
            size="sm"
            onClick={dismissAll}
            aria-label="Clear all"
            title="Clear all"
          >
            <IconTrash size={16} />
          </ActionIcon>
        </Group>
      </Group>

      {/* Content */}
      <ScrollArea.Autosize mah={400} type="auto">
        {notifications.length === 0 ? (
          <Stack align="center" py="xl" gap="xs">
            <IconBellOff size={32} color="var(--mantine-color-gray-4)" />
            <Text size="sm" c="dimmed">
              No notifications
            </Text>
          </Stack>
        ) : (
          <Box>
            {notifications.map((n) => (
              <NotificationItem
                key={n.public_id}
                notification={n}
                onMarkAsRead={markAsRead}
                onDismiss={dismiss}
                onClosePanel={onClose}
              />
            ))}
          </Box>
        )}
      </ScrollArea.Autosize>

      {/* Footer */}
      <UnstyledButton
        onClick={() => {
          navigate("/notifications");
          onClose();
        }}
        px="sm"
        py="xs"
        style={{
          display: "block",
          width: "100%",
          textAlign: "center",
          borderTop: "1px solid var(--mantine-color-gray-2)",
        }}
      >
        <Text size="xs" c="blue" fw={500}>
          View all notifications
        </Text>
      </UnstyledButton>
    </Box>
  );
}
